<template>
    <div v-if="loading" class="pf-wrap">
        <!-- Primary filters row -->
        <div class="pf-row">
            <!-- Search -->
            <div class="pf-field pf-field--search">
                <label class="pf-label">{{ $t('post.page.postsfilters.search') }}</label>
                <div class="pf-search-input">
                    <i class="fal fa-search pf-search-icon"></i>
                    <input
                        type="text"
                        v-model="filterSearch"
                        class="pf-input pf-input--search"
                        @keyup="onSearchKeyup"
                    />
                </div>
            </div>

            <!-- Form -->
            <div class="pf-field">
                <label class="pf-label">{{ $t('post.page.postsfilters.form') }}</label>
                <BmvSelect
                    v-model="filterFormulaire"
                    :options="formOptions"
                    :multiple="!singleForm"
                    label-key="name"
                    track-by="name"
                    :placeholder="' '"
                    :no-result-text="$t('post.page.postsfilters.noresult')"
                    @input="onFormFilterChange"
                />
            </div>

            <!-- Status -->
            <div class="pf-field">
                <label class="pf-label">{{ $t('post.page.postsfilters.status') }}</label>
                <BmvSelect
                    v-model="filterStatus"
                    :options="flatStatusOptions"
                    :multiple="true"
                    label-key="name"
                    track-by="name"
                    :placeholder="' '"
                    :no-result-text="$t('post.page.postsfilters.noresult')"
                    @input="onStatusFilterChange"
                />
            </div>

            <!-- Priority -->
            <div class="pf-field">
                <label class="pf-label">{{ $t('post.page.postsfilters.priority') }}</label>
                <BmvSelect
                    v-model="filterPriorite"
                    :options="priorities"
                    :multiple="true"
                    label-key="name"
                    track-by="id"
                    :placeholder="' '"
                    :no-result-text="$t('post.page.postsfilters.noresult')"
                    @input="onFilterChangeMultiSelect('priority', filterPriorite, true)"
                />
            </div>

            <!-- Toggle advanced -->
            <div class="pf-field pf-field--toggle">
                <label class="pf-label">&nbsp;</label>
                <button class="pf-advanced-btn" @click="showAdvanced = !showAdvanced">
                    <i class="fal" :class="showAdvanced ? 'fa-chevron-up' : 'fa-sliders-h'"></i>
                    <span class="pf-advanced-label">{{ showAdvanced ? 'Moins' : 'Plus' }}</span>
                </button>
            </div>
        </div>

        <!-- Advanced filters row -->
        <transition name="pf-slide">
            <div v-if="showAdvanced" class="pf-row pf-row--advanced">
                <!-- Operator -->
                <div class="pf-field">
                    <label class="pf-label">{{ $t('post.page.postsfilters.operator') }}</label>
                    <BmvSelect
                        :value="selectedOperator"
                        :options="postsFilters.operators || []"
                        label-key="name"
                        track-by="id"
                        :placeholder="' '"
                        @input="onOperatorSelect"
                    />
                </div>

                <!-- Date creation -->
                <div class="pf-field">
                    <label class="pf-label">{{ $t('post.page.postsfilters.date') }} {{ $t('post.page.postsfilters.create') }}</label>
                    <button type="button" data-toggle="dropdown" aria-expanded="false" class="pf-date-btn">
                        <span>{{ timeCreationName || '—' }}</span>
                        <i class="fal fa-chevron-down pf-date-chevron"></i>
                    </button>
                    <ul class="dropdown-menu pf-date-menu">
                        <li>
                            <div class="pf-date-option">
                                <input id="duringCreate" type="radio" value="duringCreate" v-model="radioCreate" name="dateCreation" />
                                <label for="duringCreate">{{ $t('post.page.postsfilters.date_filters.during') }}</label>
                            </div>
                            <div v-if="radioCreate == 'duringCreate'" class="pf-date-inputs">
                                <input type="number" min="0" v-model="timeDuringCreation" @change="updateTimeCreation" class="pf-input pf-input--small" />
                                <select v-model="selectTimeDuringCreation" @change="updateTimeCreation" class="pf-input pf-input--small">
                                    <option value="m">{{ $t('post.page.postsfilters.date_filters.date.minutes') }}</option>
                                    <option value="h">{{ $t('post.page.postsfilters.date_filters.date.hours') }}</option>
                                    <option value="d">{{ $t('post.page.postsfilters.date_filters.date.days') }}</option>
                                    <option value="w">{{ $t('post.page.postsfilters.date_filters.date.weeks') }}</option>
                                    <option value="mo">{{ $t('post.page.postsfilters.date_filters.date.months') }}</option>
                                </select>
                            </div>
                        </li>
                        <li>
                            <div class="pf-date-option">
                                <input id="moreCreate" type="radio" value="moreCreate" v-model="radioCreate" name="dateCreation" />
                                <label for="moreCreate">{{ $t('post.page.postsfilters.date_filters.more') }}</label>
                            </div>
                            <div v-if="radioCreate == 'moreCreate'" class="pf-date-inputs">
                                <input type="number" min="0" v-model="timeMoreCreation" @change="updateTimeCreation" class="pf-input pf-input--small" />
                                <select v-model="selectTimeMoreCreation" @change="updateTimeCreation" class="pf-input pf-input--small">
                                    <option value="m">{{ $t('post.page.postsfilters.date_filters.date.minutes') }}</option>
                                    <option value="h">{{ $t('post.page.postsfilters.date_filters.date.hours') }}</option>
                                    <option value="d">{{ $t('post.page.postsfilters.date_filters.date.days') }}</option>
                                    <option value="w">{{ $t('post.page.postsfilters.date_filters.date.weeks') }}</option>
                                    <option value="mo">{{ $t('post.page.postsfilters.date_filters.date.months') }}</option>
                                </select>
                            </div>
                        </li>
                        <li>
                            <div class="pf-date-option">
                                <input id="betweenCreate" type="radio" value="betweenCreate" v-model="radioCreate" name="dateCreation" />
                                <label for="betweenCreate">{{ $t('post.page.postsfilters.date_filters.between') }}</label>
                            </div>
                            <div v-if="radioCreate === 'betweenCreate'">
                                <date-picker v-model="timeBetweenCreation" range format="DD/MM/YYYY" :placeholder="$t('post.page.postsfilters.date_filters.select_period') || 'Sélectionnez une période'" @input="filterdatebetween(1)"></date-picker>
                            </div>
                        </li>
                        <li v-if="radioCreate" class="pf-date-reset">
                            <button class="pf-date-reset-btn" @click="resetDateCreation">
                                <i class="fal fa-times"></i> {{ $t('common.reset') || 'Réinitialiser' }}
                            </button>
                        </li>
                    </ul>
                </div>

                <!-- Date last activity -->
                <div class="pf-field">
                    <label class="pf-label">{{ $t('post.page.postsfilters.date') }} {{ $t('post.page.postsfilters.lastactivity') }}</label>
                    <button type="button" data-toggle="dropdown" aria-expanded="false" class="pf-date-btn">
                        <span>{{ timeLastActivityName || '—' }}</span>
                        <i class="fal fa-chevron-down pf-date-chevron"></i>
                    </button>
                    <ul class="dropdown-menu pf-date-menu">
                        <li>
                            <div class="pf-date-option">
                                <input id="duringLastActivity" type="radio" value="duringLastActivity" v-model="radioLastActivity" name="dateLastActivity" />
                                <label for="duringLastActivity">{{ $t('post.page.postsfilters.date_filters.during') }}</label>
                            </div>
                            <div v-if="radioLastActivity == 'duringLastActivity'" class="pf-date-inputs">
                                <input type="number" min="0" v-model="timeDuringLastActivity" @change="updateTimeLastActivity" class="pf-input pf-input--small" />
                                <select v-model="selectTimeDuringLastActivity" @change="updateTimeLastActivity" class="pf-input pf-input--small">
                                    <option value="m">{{ $t('post.page.postsfilters.date_filters.date.minutes') }}</option>
                                    <option value="h">{{ $t('post.page.postsfilters.date_filters.date.hours') }}</option>
                                    <option value="d">{{ $t('post.page.postsfilters.date_filters.date.days') }}</option>
                                    <option value="w">{{ $t('post.page.postsfilters.date_filters.date.weeks') }}</option>
                                    <option value="mo">{{ $t('post.page.postsfilters.date_filters.date.months') }}</option>
                                </select>
                            </div>
                        </li>
                        <li>
                            <div class="pf-date-option">
                                <input id="moreLastActivity" type="radio" value="moreLastActivity" v-model="radioLastActivity" name="dateLastActivity" />
                                <label for="moreLastActivity">{{ $t('post.page.postsfilters.date_filters.more') }}</label>
                            </div>
                            <div v-if="radioLastActivity == 'moreLastActivity'" class="pf-date-inputs">
                                <input type="number" min="0" v-model="timeMoreLastActivity" @change="updateTimeLastActivity" class="pf-input pf-input--small" />
                                <select v-model="selectTimeMoreLastActivity" @change="updateTimeLastActivity" class="pf-input pf-input--small">
                                    <option value="m">{{ $t('post.page.postsfilters.date_filters.date.minutes') }}</option>
                                    <option value="h">{{ $t('post.page.postsfilters.date_filters.date.hours') }}</option>
                                    <option value="d">{{ $t('post.page.postsfilters.date_filters.date.days') }}</option>
                                    <option value="w">{{ $t('post.page.postsfilters.date_filters.date.weeks') }}</option>
                                    <option value="mo">{{ $t('post.page.postsfilters.date_filters.date.months') }}</option>
                                </select>
                            </div>
                        </li>
                        <li>
                            <div class="pf-date-option">
                                <input id="betweenLastActivity" type="radio" value="betweenLastActivity" v-model="radioLastActivity" name="dateLastActivity" />
                                <label for="betweenLastActivity">{{ $t('post.page.postsfilters.date_filters.between') }}</label>
                            </div>
                            <div v-if="radioLastActivity == 'betweenLastActivity'">
                                <date-picker v-model="timeBetweenLastActivity" range format="DD/MM/YYYY" :placeholder="$t('post.page.postsfilters.date_filters.select_period') || 'Sélectionnez une période'" @input="filterdatebetween(0)"></date-picker>
                            </div>
                        </li>
                        <li v-if="radioLastActivity" class="pf-date-reset">
                            <button class="pf-date-reset-btn" @click="resetDateLastActivity">
                                <i class="fal fa-times"></i> {{ $t('common.reset') || 'Réinitialiser' }}
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </transition>
    </div>
</template>

<style scoped>
.pf-wrap {
    padding: var(--v2-space-4) var(--v2-space-5);
    border-bottom: 1px solid var(--v2-border-subtle);
    background: var(--v2-surface-0);
    font-family: var(--v2-font-sans);
}

.pf-row {
    display: flex;
    align-items: flex-start;
    gap: var(--v2-space-3);
}

.pf-row--advanced {
    margin-top: var(--v2-space-3);
    padding-top: var(--v2-space-3);
    border-top: 1px solid var(--v2-border-subtle);
}

.pf-field {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.pf-field--search {
    flex: 0 0 200px;
}

.pf-field--toggle {
    flex: 0 0 auto;
    min-width: auto;
}

.pf-label {
    display: block;
    font-size: 0.7rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-4);
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.pf-input {
    width: 100%;
    box-sizing: border-box;
    padding: 0 12px;
    height: 32px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    font-family: inherit;
    font-size: 0.82rem;
    color: var(--v2-text-1);
    background: var(--v2-surface-0);
    outline: none;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
}

.pf-input:hover {
    border-color: var(--v2-border-strong, var(--v2-border));
}

.pf-input:focus {
    border-color: var(--v2-primary) !important;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12) !important;
    outline: none !important;
}

.pf-input--small {
    padding: 0 8px;
    height: 28px;
    font-size: 0.78rem;
}

/* Search input with icon */
.pf-search-input {
    position: relative;
}

.pf-search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--v2-text-4);
    font-size: 0.78rem;
    pointer-events: none;
}

.pf-input--search {
    padding-left: 30px;
}

/* Advanced toggle */
.pf-advanced-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-surface-0);
    color: var(--v2-text-2);
    font-family: inherit;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    line-height: 1.2;
    cursor: pointer;
    white-space: nowrap;
    box-sizing: border-box;
    transition: background var(--v2-transition-fast), border-color var(--v2-transition-fast), color var(--v2-transition-fast);
}

.pf-advanced-btn:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.pf-advanced-btn i {
    font-size: 0.78rem;
}

.pf-advanced-label {
    display: none;
}

@media (min-width: 1024px) {
    .pf-advanced-label {
        display: inline;
    }
}

/* Date dropdown */
.pf-date-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-surface-0);
    font-family: inherit;
    font-size: 0.82rem;
    color: var(--v2-text-2);
    cursor: pointer;
    box-sizing: border-box;
    transition: border-color var(--v2-transition-fast), background var(--v2-transition-fast);
}

.pf-date-btn:hover {
    background: var(--v2-surface-1);
    border-color: var(--v2-border-strong, var(--v2-border));
}

.pf-date-chevron {
    font-size: 0.65rem;
    color: var(--v2-text-4);
}

.pf-date-menu {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    box-shadow: var(--v2-shadow-lg);
    padding: 6px;
    min-width: 280px;
}

.pf-date-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: var(--v2-radius-sm);
    cursor: pointer;
}

.pf-date-option:hover {
    background: var(--v2-surface-2);
}

.pf-date-option label {
    font-size: 0.82rem;
    color: var(--v2-text-2);
    cursor: pointer;
    margin: 0;
}

.pf-date-option input[type="radio"] {
    accent-color: var(--v2-primary);
}

.pf-date-reset {
    border-top: 1px solid var(--v2-border-subtle);
    padding: 6px 6px 4px;
    margin-top: 4px;
}

.pf-date-reset-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 6px 10px;
    border: none;
    border-radius: var(--v2-radius-sm);
    background: transparent;
    color: var(--v2-danger);
    font-family: inherit;
    font-size: 0.78rem;
    font-weight: var(--v2-font-weight-medium);
    cursor: pointer;
    transition: background var(--v2-transition-fast);
}

.pf-date-reset-btn:hover {
    background: rgba(239, 68, 68, 0.08);
}

.pf-date-reset-btn i {
    font-size: 0.72rem;
}

.pf-date-inputs {
    display: flex;
    gap: 6px;
    padding: 4px 10px 8px;
}

/* Transition */
.pf-slide-enter-active,
.pf-slide-leave-active {
    transition: all 0.2s ease;
    overflow: hidden;
}

.pf-slide-enter,
.pf-slide-leave-to {
    opacity: 0;
    max-height: 0;
    margin-top: 0;
    padding-top: 0;
}

@media (max-width: 900px) {
    .pf-wrap {
        padding: var(--v2-space-3) var(--v2-space-4);
    }

    .pf-row {
        flex-direction: column;
        gap: 10px;
    }

    .pf-field {
        min-width: 100%;
        flex: 0 0 100%;
    }

    .pf-field--search {
        flex: 0 0 100%;
    }
}
</style>

<script>
import { mapActions, mapState } from 'vuex'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/locale/fr'
import 'vue2-datepicker/index.css'
import BmvSelect from '@/components/UI/BmvSelect'
import moment from 'moment'

export default {
    name: 'PostsFilters',
    components: {
        DatePicker,
        BmvSelect
    },
    props: {
        singleForm: {
            type: Object,
            default: null
        },
        formTypeId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            statusnew: [],
            statusopen: [],
            statusdone: [],
            isdashboard: true,
            ismypost: false,
            isFilterStatus: true,
            filterCategorie: '',
            filterFormulaire: [],
            filterStatus: '',
            filterOperator: '',
            filterSearch: '',
            dateRange: '',
            loading: false,
            setSelectedPost: {},
            hover: false,
            timeLastActivity: null,
            timeBetweenCreation: null,
            value: '',
            filters: {},
            filterPriorite: '',
            priorities: [],
            currentFilter: {},
            sevenDaysFilter: [],
            hideDuring: true,
            radioCreate: null,
            selectTimeDuringCreation: 'm',
            timeCreationName: '',
            timeDuringCreation: null,
            timeMoreCreation: null,
            selectTimeMoreCreation: 'm',
            radioLastActivity: null,
            selectTimeDuringLastActivity: 'm',
            timeLastActivityName: '',
            timeDuringLastActivity: null,
            timeMoreLastActivity: null,
            selectTimeMoreLastActivity: 'm',
            timeBetweenLastActivity: null,
            last: true,
            calledOnce: false,
            showAdvanced: false
        }
    },
    computed: {
        ...mapState('post', {
            posts: (state) => state.posts,
            config: (state) => state.postsConfig,
            post: (state) => state.post,
            postsFilters: (state) => state.postsFilters
        }),
        ...mapState('auth', {
            user: (state) => state.user
        }),
        ...mapState('admin', {
            userImpersonated: (state) => state.userImpersonated
        }),
        ...mapState('form', {
            allForms: (state) => state.forms,
            formTypes: (state) => state.types
        }),
        formOptions() {
            const filterForms = this.postsFilters && this.postsFilters.forms
                ? this.postsFilters.forms
                : []
            if (!this.allForms) return filterForms

            let allowedFormIds
            if (this.formTypeId) {
                // PostsByFormType: forms of that specific type
                allowedFormIds = this.allForms
                    .filter((f) => f.form_type_id === parseInt(this.formTypeId))
                    .map((f) => f.id)
            } else if (this.formTypes) {
                // Posts: only forms from listable types
                const listableTypeIds = this.formTypes
                    .filter((t) => t.listable)
                    .map((t) => t.id)
                allowedFormIds = this.allForms
                    .filter((f) => listableTypeIds.includes(f.form_type_id))
                    .map((f) => f.id)
            }

            if (allowedFormIds) {
                return filterForms.filter((f) => allowedFormIds.includes(f.id))
            }
            return filterForms
        },
        selectedOperator() {
            if (!this.filterOperator || !this.postsFilters.operators) return null
            return this.postsFilters.operators.find((o) => o.id === this.filterOperator) || null
        },
        flatStatusOptions() {
            const opts = this.statusOptions
            if (!opts || !opts.length) return []
            // If grouped, flatten
            if (opts[0] && opts[0].statuses) {
                const flat = []
                opts.forEach((group) => {
                    (group.statuses || []).forEach((s) => {
                        if (!flat.some((f) => f.name === s.name)) flat.push(s)
                    })
                })
                return flat
            }
            return opts
        },
        isGrouped() {
            return !this.singleForm && Array.isArray(this.filterFormulaire) && this.filterFormulaire.length > 1
        },
        statusOptions() {
            const selectedForms = this.singleForm
                ? [this.filterFormulaire].filter(Boolean)
                : this.filterFormulaire

            const hasSelection = selectedForms && (Array.isArray(selectedForms) ? selectedForms.length > 0 : !!selectedForms)

            // Determine which forms to pull statuses from
            const forms = this.allForms || []
            let targetForms = forms

            if (hasSelection) {
                const formIds = selectedForms.map((f) => f.id)
                targetForms = forms.filter((f) => formIds.includes(f.id))
            }

            // Grouped mode: 2+ forms selected in multi-select
            if (this.isGrouped && targetForms.length > 1) {
                return targetForms
                    .filter((f) => f.statuses && f.statuses.length > 0)
                    .map((form) => ({
                        formName: form.name,
                        statuses: [...form.statuses].sort((a, b) =>
                            a.name.localeCompare(b.name)
                        )
                    }))
            }

            // Flat mode: merge & deduplicate by name
            const statusByName = {}
            targetForms.forEach((form) => {
                if (form.statuses) {
                    form.statuses.forEach((s) => {
                        if (!statusByName[s.name]) {
                            statusByName[s.name] = {
                                id: s.id,
                                name: s.name,
                                ids: [s.id]
                            }
                        } else {
                            statusByName[s.name].ids.push(s.id)
                        }
                    })
                }
            })
            const statuses = Object.values(statusByName)

            if (statuses.length > 0) {
                return statuses.sort((a, b) => a.name.localeCompare(b.name))
            }

            // Last resort fallback if allForms not loaded yet
            const fallback = this.postsFilters && this.postsFilters.statuses
                ? this.postsFilters.statuses
                : []
            return [...fallback].sort((a, b) => a.name.localeCompare(b.name))
        }
    },
    watch: {
        filterOperator: function (newValue) {
            this.calledOnce = false
            this.currentFilter.with_operator = newValue
        },
        filterStatus: function (newValue) {
            this.calledOnce = false
            if (newValue.length === 0) {
                this.currentFilter.status_id = ''
            }
        },
        filterSearch: function (newValue) {
            this.calledOnce = false
        }
    },
    created() {
        if (this.formTypeId) {
            this.filters.form_type_id = this.formTypeId
            this.currentFilter.form_type_id = this.formTypeId
        }
        if (!this.allForms) {
            this.getForms()
        }
        this.getFilters()
        for (let i = 0; i < 5; i++) {
            this.priorities.push({
                name: this.$t(`post.page.priority.${i}`),
                id: i
            })
        }
        this.sevenDaysFilter = [
            moment().subtract(6, 'd').toDate(),
            moment().add(1, 'd').toDate()
        ]
    },
    methods: {
        ...mapActions('post', [
            'getPosts',
            'getPost',
            'updateStatus',
            'getPostsFilters'
        ]),
        ...mapActions('form', ['getForms']),
        onOperatorSelect(operator) {
            this.filterOperator = operator ? operator.id : ''
            this.onFilterChange({ with_operator: this.filterOperator })
        },
        resetDateCreation() {
            this.radioCreate = null
            this.timeDuringCreation = null
            this.timeMoreCreation = null
            this.timeBetweenCreation = null
            this.timeCreationName = ''
            this.currentFilter.created_date_between = ''
            this.onFilterChange(this.currentFilter)
        },
        resetDateLastActivity() {
            this.radioLastActivity = null
            this.timeDuringLastActivity = null
            this.timeMoreLastActivity = null
            this.timeBetweenLastActivity = null
            this.timeLastActivityName = ''
            this.currentFilter.updated_date_between = ''
            this.onFilterChange(this.currentFilter)
        },
        onSearchKeyup() {
            if ((this.filterSearch !== '' && this.filterSearch.length >= 3) || this.filterSearch === '') {
                this.onFilterChange({ search: this.filterSearch })
            }
        },
        getFilters() {
            this.loading = false
            this.getPostsFilters()
                .then(() => {
                    if (this.singleForm) {
                        this.filterFormulaire = this.singleForm
                        this.currentFilter.form_id = this.singleForm.id
                        this.onFilterChange(this.currentFilter)
                    }
                    if (Object.keys(this.$route.query).length !== 0) {
                        // status dashoard
                        this.statusnew = [this.postsFilters.statuses[0]]
                        this.statusopen = [
                            this.postsFilters.statuses[0],
                            this.postsFilters.statuses[1],
                            this.postsFilters.statuses[2]
                        ]
                        this.statusdone = [
                            this.postsFilters.statuses[3],
                            this.postsFilters.statuses[4],
                            this.postsFilters.statuses[5],
                            this.postsFilters.statuses[6],
                            this.postsFilters.statuses[7]
                        ]
                        if (this.$route.query.form_id) {
                            this.filterFormulaire.push(
                                this.postsFilters.forms.filter(
                                    (form) =>
                                        form.id ===
                                        parseInt(this.$route.query.form_id)
                                )[0]
                            )
                            this.currentFilter.form_id =
                                this.$route.query.form_id
                        }

                        if (this.$route.query.status) {
                            switch (this.$route.query.status) {
                                // Dashboard : mes derniers tickets
                                case 'mylastposts':
                                    this.ismypost = true
                                    this.isFilterStatus = false
                                    break
                                case 'mynewposts':
                                    this.filterStatus = this.statusnew
                                    this.ismypost = true
                                    break
                                case 'myopenedposts':
                                    this.filterStatus = this.statusopen
                                    this.ismypost = true
                                    break
                                case 'mydoneposts':
                                    this.filterStatus = this.statusdone
                                    this.ismypost = true
                                    break
                                // Dashboard : derniers tickets
                                case 'lastposts':
                                    this.isFilterStatus = false
                                    break
                                case 'newposts':
                                    this.filterStatus = this.statusnew
                                    break
                                case 'openedposts':
                                    this.filterStatus = this.statusopen
                                    break
                                case 'doneposts':
                                    this.filterStatus = this.statusdone
                                    break
                                // Menu - tickets nouveaux et tickets ouverts
                                case 'new':
                                    this.filterStatus = this.statusnew
                                    this.isdashboard = false
                                    break
                                case 'open':
                                    this.filterStatus = this.statusopen
                                    this.isdashboard = false
                                    break
                                default:
                                    break
                            }
                            if (this.isFilterStatus) {
                                this.currentFilter =
                                    this.onFilterChangeMultiSelect(
                                        'status_id',
                                        this.filterStatus
                                    )
                            }
                            if (this.isdashboard && this.last) {
                                this.timeBetweenCreation = this.sevenDaysFilter
                                this.radioCreate = 'betweenCreate'
                                this.filterdatebetween(1)
                                this.currentFilter.created_date_between =
                                    this.formatDateFilter(this.sevenDaysFilter)
                            }
                            if (this.ismypost) {
                                this.filterOperator = this.userImpersonated
                                    ? this.userImpersonated.id
                                    : this.user.id
                                this.currentFilter.with_operator = this
                                    .userImpersonated
                                    ? this.userImpersonated.id
                                    : this.user.id
                            }
                        }

                        if (this.$route.query.responsability) {
                            switch (this.$route.query.responsability) {
                                case 'operator':
                                    this.filterOperator = this.userImpersonated
                                        ? this.userImpersonated.id
                                        : this.user.id
                                    this.currentFilter.with_operator = this
                                        .userImpersonated
                                        ? this.userImpersonated.id
                                        : this.user.id
                                    break
                                case 'informed':
                                    this.currentFilter.with_user_informed = this
                                        .userImpersonated
                                        ? this.userImpersonated.id
                                        : this.user.id
                                    break
                                default:
                                    break
                            }
                        }
                        // priority dashoard
                        if (this.$route.query.priority) {
                            let index = this.priorities.findIndex(
                                (priority) =>
                                    priority.id ===
                                    parseInt(this.$route.query.priority)
                            )
                            this.filterPriorite = this.priorities[index]
                            this.currentFilter = this.onFilterChangeMultiSelect(
                                'status_id',
                                this.statusopen
                            )
                            this.currentFilter.priority = this.filterPriorite.id
                            this.filterOperator = this.userImpersonated
                                ? this.userImpersonated.id
                                : this.user.id
                            this.currentFilter.with_operator = this
                                .userImpersonated
                                ? this.userImpersonated.id
                                : this.user.id
                        }

                        this.onFilterChange(this.currentFilter)

                    } else {
                        this.currentFilter.with_operator = ''
                        this.onFilterChange(this.currentFilter)
                    }
                })
                .catch((e) => {
                    this.$store.dispatch('dispatchError', e)
                })
                .finally(() => {
                    this.loading = true
                })
        },
        onStatusFilterChange() {
            const selected = Array.isArray(this.filterStatus)
                ? this.filterStatus
                : [this.filterStatus]
            const allIds = []
            selected.forEach((s) => {
                if (s.ids && s.ids.length > 0) {
                    allIds.push(...s.ids)
                } else {
                    allIds.push(s.id)
                }
            })
            if (allIds.length > 0) {
                this.onFilterChange({ status_id: allIds.toString() })
            } else {
                this.onFilterChange({ status_id: '' })
            }
        },
        onFormFilterChange() {
            this.filterStatus = this.singleForm ? '' : []
            this.currentFilter.status_id = ''
            if (this.singleForm) {
                this.$emit('form-changed', this.filterFormulaire)
                this.onFilterChangeMultiSelect(
                    'form_id',
                    [this.filterFormulaire],
                    true
                )
            } else {
                this.onFilterChangeMultiSelect(
                    'form_id',
                    this.filterFormulaire,
                    true
                )
            }
        },
        onFilterChangeMultiSelect(element, filter, change) {
            if (filter.length) {
                var transformedFilter = Array()
                for (let i = 0; i < filter.length; i++) {
                    transformedFilter[i] = filter[i].id
                }
                filter = {
                    [element]: transformedFilter.toString()
                }
            } else {
                filter = {
                    [element]: filter.id
                }
            }
            if (change) {
                this.onFilterChange(filter)
            } else {
                return filter
            }
        },
        onFilterChange(currentFilter) {
            const $ = this
            const search = this.filterSearch
            delete currentFilter.search
            Object.keys(currentFilter).forEach((element) => {
                this.filters[element] = currentFilter[element]
            })
            setTimeout(() => {
                $.config.per_page = '15'
                $.config.page = 1
                $.config.filters = $.filters
                $.loadAsyncData(search)
            })
        },
        loadAsyncData(search = '') {
            this.$emit('filterpost', search)
            if (!this.calledOnce) {
                this.getPosts(search)
                    .then(() => {
                        if (
                            this.posts.data.length == 0 &&
                            this.filters.search.length > 0
                        ) {
                            // this.filters.content = this.filters.search
                            this.filters.search = ''
                            this.loadAsyncData()
                            // this.filters.content = ''
                            this.calledOnce = true
                        }
                        this.$emit('loadpage')
                    })
                    .catch((e) => {
                        if (this.posts.data.length !== 0) {
                            this.$store.dispatch('dispatchError', e)
                        }
                    })
            }
        },
        filterdatebetween(selectdatetype) {
            if (this.timeBetweenLastActivity && selectdatetype == 0) {
                this.timeCreationName = ''
                this.timeLastActivityName = `${moment(
                    this.timeBetweenLastActivity[0]
                ).format('YYYY-MM-DD')} - ${moment(
                    this.timeBetweenLastActivity[1]
                ).format('YYYY-MM-DD')}`
                this.currentFilter.created_date_between = ''
                this.currentFilter.updated_date_between = this.formatDateFilter(
                    this.timeBetweenLastActivity
                )
                this.timeBetweenCreation = null
            } else if (this.timeBetweenCreation && selectdatetype == 1) {
                this.timeLastActivityName = ''
                this.timeCreationName = `${moment(
                    this.timeBetweenCreation[0]
                ).format('YYYY-MM-DD')} - ${moment(
                    this.timeBetweenCreation[1]
                ).format('YYYY-MM-DD')}`
                this.currentFilter.updated_date_between = ''
                this.currentFilter.created_date_between = this.formatDateFilter(
                    this.timeBetweenCreation
                )
                this.timeBetweenLastActivity = null
            }
            this.onFilterChange(this.currentFilter)
            if (
                this.timeBetweenCreation.includes(null) &&
                this.timeBetweenLastActivity.includes(null)
            ) {
                this.currentFilter.updated_date_between = ''
                this.currentFilter.created_date_between = ''
                this.onFilterChange(this.currentFilter)
            }
        },
        formatDateFilter(filter) {
            return `${moment(filter[0]).format('YYYY-MM-DD 00:00')},${moment(
                filter[1]
            ).format('YYYY-MM-DD 23:59')}`
        },
        formatDateDuring(date, format) {
            if (format === 'minute' || format === 'hour') {
                return `${moment()
                    .locale('fr')
                    .utc()
                    .subtract(date, format)
                    .format('YYYY-MM-DD LT')},${moment()
                    .locale('fr')
                    .utc()
                    .format('YYYY-MM-DD LT')}`
            }
            return `${moment()
                .locale('fr')
                .utc()
                .subtract(date, format)
                .format('YYYY-MM-DD 00:00')},${moment()
                .locale('fr')
                .utc()
                .format('YYYY-MM-DD 23:59')}`
        },
        formatDateMore(date, format) {
            if (format === 'minute' || format === 'hour') {
                return `${moment()
                    .locale('fr')
                    .utc()
                    .subtract(100, 'year')
                    .format('YYYY-MM-DD 00:00')},${moment()
                    .locale('fr')
                    .utc()
                    .subtract(date, format)
                    .format('YYYY-MM-DD LT')}`
            }
            return `${moment()
                .locale('fr')
                .utc()
                .subtract(100, 'year')
                .format('YYYY-MM-DD 00:00')},${moment()
                .locale('fr')
                .utc()
                .subtract(date, format)
                .format('YYYY-MM-DD 23:59')}`
        },
        changeTimeCreationName(event, format) {
            if (event === 'during') {
                this.timeCreationName = this.timeDuringCreation
                    ? `${this.$t(
                          `post.page.postsfilters.date_filters.${event}`
                      )} ${this.timeDuringCreation} ${this.$t(
                          `post.page.postsfilters.date_filters.date.${format}`
                      )}`
                    : ''
            } else {
                this.timeCreationName = this.timeMoreCreation
                    ? `${this.$t(
                          `post.page.postsfilters.date_filters.${event}`
                      )} ${this.timeDuringCreation} ${this.$t(
                          `post.page.postsfilters.date_filters.date.${format}`
                      )}`
                    : ''
            }
        },
        changeTimeLastActivityName(event, format) {
            if (event === 'during') {
                this.timeLastActivityName = this.timeDuringLastActivity
                    ? `${this.$t(
                          `post.page.postsfilters.date_filters.${event}`
                      )} ${this.timeDuringLastActivity} ${this.$t(
                          `post.page.postsfilters.date_filters.date.${format}`
                      )}`
                    : ''
            } else {
                this.timeLastActivityName = this.timeMoreLastActivity
                    ? `${this.$t(
                          `post.page.postsfilters.date_filters.${event}`
                      )} ${this.timeDuringLastActivity} ${this.$t(
                          `post.page.postsfilters.date_filters.date.${format}`
                      )}`
                    : ''
            }
        },
        updateTimeCreation(event) {
            this.currentFilter.updated_date_between = ''
            this.timeLastActivityName = ''
            this.timeCreationName = ''

            switch (this.radioCreate) {
                case 'duringCreate':
                    if (this.timeDuringCreation) {
                        switch (this.selectTimeDuringCreation) {
                            case 'm':
                                this.currentFilter.created_date_between =
                                    this.formatDateDuring(
                                        this.timeDuringCreation,
                                        'minute'
                                    )
                                this.changeTimeCreationName('during', 'minutes')
                                break
                            case 'h':
                                this.currentFilter.created_date_between =
                                    this.formatDateDuring(
                                        this.timeDuringCreation,
                                        'hour'
                                    )
                                this.changeTimeCreationName('during', 'hours')
                                break
                            case 'd':
                                this.currentFilter.created_date_between =
                                    this.formatDateDuring(
                                        this.timeDuringCreation,
                                        'day'
                                    )
                                this.changeTimeCreationName('during', 'days')
                                break
                            case 'w':
                                this.currentFilter.created_date_between =
                                    this.formatDateDuring(
                                        this.timeDuringCreation,
                                        'week'
                                    )
                                this.changeTimeCreationName('during', 'weeks')
                                break
                            case 'mo':
                                this.currentFilter.created_date_between =
                                    this.formatDateDuring(
                                        this.timeDuringCreation,
                                        'month'
                                    )
                                this.changeTimeCreationName('during', 'months')
                                break
                        }
                        break
                    } else {
                        this.currentFilter.created_date_between = ''
                    }
                case 'moreCreate':
                    if (this.timeMoreCreation) {
                        switch (this.selectTimeMoreCreation) {
                            case 'm':
                                this.currentFilter.created_date_between =
                                    this.formatDateMore(
                                        this.timeMoreCreation,
                                        'minute'
                                    )
                                this.changeTimeCreationName('more', 'minutes')
                                break
                            case 'h':
                                this.currentFilter.created_date_between =
                                    this.formatDateMore(
                                        this.timeMoreCreation,
                                        'hour'
                                    )
                                this.changeTimeCreationName('more', 'hours')
                                break
                            case 'd':
                                this.currentFilter.created_date_between =
                                    this.formatDateMore(
                                        this.timeMoreCreation,
                                        'day'
                                    )
                                this.changeTimeCreationName('more', 'days')
                                break
                            case 'w':
                                this.currentFilter.created_date_between =
                                    this.formatDateMore(
                                        this.timeMoreCreation,
                                        'week'
                                    )
                                this.changeTimeCreationName('more', 'weeks')
                                break
                            case 'mo':
                                this.currentFilter.created_date_between =
                                    this.formatDateMore(
                                        this.timeMoreCreation,
                                        'month'
                                    )
                                this.changeTimeCreationName('more', 'months')
                                break
                        }
                        break
                    } else {
                        this.currentFilter.created_date_between = ''
                    }
            }

            this.onFilterChange(this.currentFilter)
        },
        updateTimeLastActivity(event) {
            this.currentFilter.created_date_between = ''
            this.timeCreationName = ''
            this.timeLastActivityName = ''

            switch (this.radioLastActivity) {
                case 'duringLastActivity':
                    if (this.timeDuringLastActivity) {
                        switch (this.selectTimeDuringLastActivity) {
                            case 'm':
                                this.currentFilter.updated_date_between =
                                    this.formatDateDuring(
                                        this.timeDuringLastActivity,
                                        'minute'
                                    )
                                this.changeTimeLastActivityName(
                                    'during',
                                    'minutes'
                                )
                                break
                            case 'h':
                                this.currentFilter.updated_date_between =
                                    this.formatDateDuring(
                                        this.timeDuringLastActivity,
                                        'hour'
                                    )
                                this.changeTimeLastActivityName(
                                    'during',
                                    'hours'
                                )
                                break
                            case 'd':
                                this.currentFilter.updated_date_between =
                                    this.formatDateDuring(
                                        this.timeDuringLastActivity,
                                        'day'
                                    )
                                this.changeTimeLastActivityName(
                                    'during',
                                    'days'
                                )
                                break
                            case 'w':
                                this.currentFilter.updated_date_between =
                                    this.formatDateDuring(
                                        this.timeDuringLastActivity,
                                        'week'
                                    )
                                this.changeTimeLastActivityName(
                                    'during',
                                    'weeks'
                                )
                                break
                            case 'mo':
                                this.currentFilter.updated_date_between =
                                    this.formatDateDuring(
                                        this.timeDuringLastActivity,
                                        'month'
                                    )
                                this.changeTimeLastActivityName(
                                    'during',
                                    'months'
                                )
                                break
                        }
                        break
                    } else {
                        this.currentFilter.updated_date_between = ''
                    }
                case 'moreLastActivity':
                    if (this.timeMoreLastActivity) {
                        switch (this.selectTimeMoreLastActivity) {
                            case 'm':
                                this.currentFilter.updated_date_between =
                                    this.formatDateMore(
                                        this.timeMoreLastActivity,
                                        'minute'
                                    )
                                this.changeTimeLastActivityName(
                                    'more',
                                    'minutes'
                                )
                                break
                            case 'h':
                                this.currentFilter.updated_date_between =
                                    this.formatDateMore(
                                        this.timeMoreLastActivity,
                                        'hour'
                                    )
                                this.changeTimeLastActivityName('more', 'hours')
                                break
                            case 'd':
                                this.currentFilter.updated_date_between =
                                    this.formatDateMore(
                                        this.timeMoreLastActivity,
                                        'day'
                                    )
                                this.changeTimeLastActivityName('more', 'days')
                                break
                            case 'w':
                                this.currentFilter.updated_date_between =
                                    this.formatDateMore(
                                        this.timeMoreLastActivity,
                                        'week'
                                    )
                                this.changeTimeLastActivityName('more', 'weeks')
                                break
                            case 'mo':
                                this.currentFilter.updated_date_between =
                                    this.formatDateMore(
                                        this.timeMoreLastActivity,
                                        'month'
                                    )
                                this.changeTimeLastActivityName(
                                    'more',
                                    'months'
                                )
                                break
                        }
                        break
                    } else {
                        this.currentFilter.updated_date_between = ''
                    }
            }

            this.onFilterChange(this.currentFilter)
        }
    },
    mounted() {
        this.loadAsyncData()
    }
}
</script>
